import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/pinia.js'

import LandingView from '@/views/LandingView.vue'
import LoginView from '@/views/LoginView.vue'
import VerifyEmailView from '@/views/VerifyEmailView.vue'
import ForgotPasswordView from '@/views/ForgotPasswordView.vue'
import ResetPasswordView from '@/views/ResetPasswordView.vue'
import SetPasswordView from '@/views/SetPasswordView.vue'
import ChangePasswordView from '@/views/ChangePasswordView.vue'
import PrivacyPolicyView from '@/views/PrivacyPolicyView.vue'
import PublicOfferView from '@/views/PublicOfferView.vue'
import ThanksView from '@/views/ThanksView.vue'
import AccountLockedView from '@/views/AccountLockedView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import { canUseProduct } from '@/utils/productAccess.js'

const SearchAnalysis = () => import('@/components/SearchAnalysis.vue')
const ListsWorkspaceView = () => import('@/views/ListsWorkspaceView.vue')
const FavoritesView = () => import('@/views/FavoritesView.vue')
const AdminDashboardView = () => import('@/views/admin/AdminDashboardView.vue')
const AdminFeedbackView = () => import('@/views/admin/AdminFeedbackView.vue')

const PRODUCT_ROUTES = ['/search', '/lists', '/favorites']

const routes = [
  {
    path: '/',
    component: LandingView,
    meta: { marketingShell: true },
  },
  {
    path: '/login',
    component: LoginView,
    meta: { marketingShell: true },
  },
  {
    path: '/register',
    redirect: () => ({ path: '/login', query: { invite: 'only' } }),
  },
  {
    path: '/verify-email',
    component: VerifyEmailView,
    meta: { marketingShell: true },
  },
  {
    path: '/forgot-password',
    component: ForgotPasswordView,
    meta: { marketingShell: true },
  },
  {
    path: '/reset-password',
    component: ResetPasswordView,
    meta: { marketingShell: true },
  },
  {
    path: '/set-password',
    component: SetPasswordView,
    meta: { marketingShell: true },
  },
  {
    path: '/change-password',
    component: ChangePasswordView,
    meta: { marketingShell: true },
  },
  {
    path: '/privacy',
    component: PrivacyPolicyView,
    meta: { marketingShell: true },
  },
  {
    path: '/offer',
    component: PublicOfferView,
    meta: { marketingShell: true },
  },
  {
    path: '/thanks',
    component: ThanksView,
    meta: { marketingShell: true },
  },

  {
    path: '/admin',
    redirect: '/admin/dashboard',
  },
  {
    path: '/admin/dashboard',
    component: AdminDashboardView,
    meta: { requiresAuth: true, requiresStaff: true, productDensity: true },
  },
  {
    path: '/admin/feedback',
    component: AdminFeedbackView,
    meta: { requiresAuth: true, requiresStaff: true, productDensity: true },
  },

  {
    path: '/search',
    component: SearchAnalysis,
    meta: { requiresAuth: true, productDensity: true },
  },
  {
    path: '/lists',
    component: ListsWorkspaceView,
    meta: { requiresAuth: true, fullWidth: true, productDensity: true },
  },
  {
    path: '/favorites',
    component: FavoritesView,
    meta: { requiresAuth: true, productDensity: true },
  },
  {
    path: '/account',
    component: AccountLockedView,
    meta: { requiresAuth: true, accountShell: true },
  },
  {
    path: '/:pathMatch(.*)*',
    component: NotFoundView,
    meta: { marketingShell: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

const guestOnlyPaths = ['/login', '/forgot-password']

async function ensureProfile(auth) {
  if (!auth.user) {
    try {
      await auth.fetchProfile()
    } catch {
      return false
    }
  }
  return Boolean(auth.user)
}

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (to.meta.requiresAuth && !auth.isAuth) return '/login'

  if (auth.isAuth && (to.meta.requiresAuth || to.meta.requiresStaff)) {
    const ok = await ensureProfile(auth)
    if (!ok) return '/login'
  }

  const productOk = canUseProduct(auth.user)

  if (auth.isAuth && guestOnlyPaths.includes(to.path)) {
    return productOk ? '/search' : '/account'
  }

  if (auth.isAuth && PRODUCT_ROUTES.includes(to.path) && !productOk) {
    return '/account'
  }

  if (auth.isAuth && to.path === '/account' && productOk) {
    if (to.query?.billing) {
      return { path: '/search', query: { ...to.query } }
    }
    return '/search'
  }

  if (to.meta.requiresStaff) {
    if (!auth.isAuth) return '/login'
    if (!auth.user?.is_staff) return productOk ? '/search' : '/account'
  }
})

export default router
